import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { RotateCw, Lock } from "lucide-react";
import { fmtDate } from "@/lib/api";

const STATUS_TEXT = { ACTIVE: "Active", DEMO_ACTIVE: "Demo — not valid", EXPIRED: "Expired", PENDING_PAYMENT: "Awaiting activation" };

export function DigitalCard({ cp, holder }) {
  const [flipped, setFlipped] = useState(false);
  const locked = !["ACTIVE", "DEMO_ACTIVE", "EXPIRED"].includes(cp.status);
  const demo = cp.status === "DEMO_ACTIVE";
  return (
    <div className="[perspective:1200px]" data-testid={`digital-card-${cp.card_serial}`}>
      <div className={`flip-inner relative aspect-[1.586/1] w-full ${flipped ? "flipped" : ""}`}>
        <div className={`flip-face card-shine absolute inset-0 overflow-hidden rounded-[22px] p-5 text-white shadow-xl shadow-purple-900/25 gradient-card ${locked ? "grayscale-[0.6] opacity-80" : ""}`}>
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-white/10 blur-2xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-blue-400/20 blur-2xl" />
          {demo && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span className="-rotate-12 rounded-md border-2 border-amber-300/80 px-3 py-1 text-lg font-black tracking-[0.3em] text-amber-200/90" data-testid="card-demo-watermark">DEMO</span>
            </div>
          )}
          <div className="relative flex h-full flex-col justify-between">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2">
                <img src="/logo.png" alt="" className="h-9 w-9 rounded-full bg-white/90 p-0.5" />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70">GIA Healthcare</p>
                  <p className="text-sm font-extrabold leading-tight">{cp.package_name}</p>
                </div>
              </div>
              <span className="rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold backdrop-blur" data-testid="card-status-label">
                {STATUS_TEXT[cp.status] || cp.status}
              </span>
            </div>
            <div className="h-7 w-10 rounded-md bg-gradient-to-br from-amber-200 to-amber-400/80 shadow-inner" />
            <div>
              <p className="font-mono text-lg font-semibold tracking-[0.12em] sm:text-xl" data-testid="card-serial">{cp.card_serial}</p>
              <div className="mt-2 flex items-end justify-between text-[11px]">
                <div>
                  <p className="uppercase tracking-wider text-white/60">Member</p>
                  <p className="font-semibold">{holder}</p>
                </div>
                <div className="text-right">
                  <p className="uppercase tracking-wider text-white/60">Valid until</p>
                  <p className="font-semibold" data-testid="card-valid-until">{locked ? "—" : fmtDate(cp.expires_at)}</p>
                </div>
              </div>
            </div>
          </div>
          {locked && (
            <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 backdrop-blur-[2px]">
              <span className="flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-xs font-bold text-slate-800"><Lock size={13} /> Awaiting verified activation</span>
            </div>
          )}
        </div>
        <div className="flip-face flip-back absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-[22px] border border-slate-100 bg-white p-5 shadow-xl">
          <QRCodeSVG value={`GIACARE:${cp.card_serial}`} size={120} fgColor="#4C1D95" data-testid="card-qr" />
          <p className="font-mono text-xs text-slate-500">{cp.card_serial}</p>
          <p className="text-[10px] text-slate-400">Show to your GIA provider for identification only</p>
        </div>
      </div>
      {!locked && (
        <button className="btn-ghost btn-sm mx-auto mt-3 flex" onClick={() => setFlipped((f) => !f)} data-testid={`card-flip-${cp.card_serial}`}>
          <RotateCw size={14} /> {flipped ? "Show front" : "Show member QR"}
        </button>
      )}
    </div>
  );
}
